import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Pressable,
  ScrollView,
  View,
  Text,
  TextInput,
  useWindowDimensions,
  ActivityIndicator,
  Alert,
} from "react-native";
import {
  X,
  ChevronLeft,
  Camera,
  Calendar,
  Clock,
  Stethoscope,
  Syringe,
  MessageSquare,
  Heart,
  Video,
  MapPin,
  Link,
  FileText,
  Timer,
  Droplets,
  Scan,
  Scissors,
  Dna,
  ShieldCheck,
  Thermometer,
  FlaskConical,
  Activity,
  CircleDot,
  Egg,
  ArrowRightLeft,
  type LucideIcon,
} from "lucide-react-native";
import * as ImagePicker from "expo-image-picker";
import {
  MoonDateField,
  MoonFormField,
  MoonTimeField,
  Tag,
  ActionRowCard,
  Dialog,
  DialogActions,
  DialogIcon,
  DialogHeader,
  DialogPrimaryButton,
  SoftSecondaryButton,
  MoonFieldInput,
  MoonFilterChip,
  MoonSearchInput,
} from "@louise/moon-ui";
import {
  calculateAppointmentDurationMinutes,
  calculateAppointmentEnd,
  useCreateAppointment,
  useUpdateAppointment,
} from "@src/packages/care";
import {
  projectDocumentProcessingRunToPrefill,
  useDocumentSourceTransfer,
  type DocumentProcessingPrefillAppointment,
} from "@src/packages/documents/ingestion";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";
import { validateAppointmentDraft } from "@src/packages/schemas";
import { useTimezone } from "@src/packages/preferences";
import {
  formatDate,
  formatTimeHHMM,
  useLocale,
  useTranslations,
} from "@src/packages/i18n";
import enLocale from "@src/packages/i18n/locales/en.json";
import { EMBER } from "../../../../onboarding/theme";
import { localizeAppointmentTitle } from "../localizeAppointmentTitle";
import type { AppointmentScheduleItem } from "../schedule.types";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// ============================================================================
// Types
// ============================================================================

type Props = {
  visible: boolean;
  onClose: () => void;
  editAppointment?: (AppointmentScheduleItem & { date: string }) | null;
  /**
   * Seed the form for a brand-new appointment (a "create", not an edit) so a
   * caller can launch it pre-filled — e.g. the two-week-wait card opening a
   * beta hCG blood test. Ignored when `editAppointment` is set.
   */
  prefill?: {
    type?: AppointmentType;
    activities?: string[];
    title?: string;
    date?: string;
    cycleId?: string | null;
  } | null;
  /**
   * Fires after a successful create/update with the saved date and subtypes.
   * Lets a caller keep related state in sync — e.g. the two-week-wait card
   * moving the pregnancy-test date when a beta hCG appointment is saved.
   */
  onSaved?: (saved: {
    appointmentId: string | null;
    startDate: string;
    startTime: string | null;
    consultationType: string[] | null;
  }) => void;
  onLocalSave?: (appt: {
    title: string;
    consultationType: string[] | null;
    startDate: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
    timeZone?: string;
    location?: string;
    notes?: string;
  }) => void;
};

type AppointmentType = "exam" | "procedure" | "consult" | "counseling";

interface SubtypeItem {
  /** Canonical English name — used as the API/storage key. Display label resolved via labelKey at render time. */
  name: string;
  labelKey: string;
  icon: LucideIcon;
}

interface Section {
  key: string;
  labelKey: string;
  icon: LucideIcon;
  items: SubtypeItem[];
}

const DURATION_PRESETS = [30, 45, 60, 90] as const;
const DEFAULT_DURATION_MINUTES = 60;
type DurationSelection = (typeof DURATION_PRESETS)[number] | "custom";

const APPOINTMENT_ILLUSTRATION =
  require("../../../../assets/images/treatment-schedule.png") as number;

// ============================================================================
// Data
// ============================================================================

const TYPE_OPTIONS: {
  value: AppointmentType;
  labelKey: string;
  icon: LucideIcon;
}[] = [
  { value: "exam", labelKey: "add_appointment.type.exam", icon: Stethoscope },
  {
    value: "procedure",
    labelKey: "add_appointment.type.procedure",
    icon: Syringe,
  },
  {
    value: "consult",
    labelKey: "add_appointment.type.consult",
    icon: MessageSquare,
  },
  {
    value: "counseling",
    labelKey: "add_appointment.type.counseling",
    icon: Heart,
  },
];

const EXAM_SECTIONS: Section[] = [
  {
    key: "blood-lab",
    labelKey: "add_appointment.exam_section.blood_lab",
    icon: Droplets,
    items: [
      {
        name: "Beta hCG",
        labelKey: "add_appointment.exam_section.item.beta_hcg",
        icon: Droplets,
      },
      {
        name: "Hormonal testing",
        labelKey: "add_appointment.exam_section.item.hormonal_testing",
        icon: Droplets,
      },
      {
        name: "STD testing",
        labelKey: "add_appointment.exam_section.item.std_testing",
        icon: ShieldCheck,
      },
      {
        name: "Thyroid testing",
        labelKey: "add_appointment.exam_section.item.thyroid_testing",
        icon: Thermometer,
      },
      {
        name: "Urinalysis",
        labelKey: "add_appointment.exam_section.item.urinalysis",
        icon: FlaskConical,
      },
      {
        name: "Semen analysis",
        labelKey: "add_appointment.exam_section.item.semen_analysis",
        icon: FlaskConical,
      },
      {
        name: "Swab test",
        labelKey: "add_appointment.exam_section.item.swab_test",
        icon: FlaskConical,
      },
      {
        name: "Pap smear",
        labelKey: "add_appointment.exam_section.item.pap_smear",
        icon: ShieldCheck,
      },
    ],
  },
  {
    key: "imaging",
    labelKey: "add_appointment.exam_section.imaging",
    icon: Scan,
    items: [
      {
        name: "Transabdominal ultrasound",
        labelKey: "add_appointment.exam_section.item.transabdominal_ultrasound",
        icon: Scan,
      },
      {
        name: "Transvaginal ultrasound",
        labelKey: "add_appointment.exam_section.item.transvaginal_ultrasound",
        icon: Scan,
      },
      {
        name: "Doppler ultrasound",
        labelKey: "add_appointment.exam_section.item.doppler_ultrasound",
        icon: Activity,
      },
      {
        name: "Hysterosonography",
        labelKey: "add_appointment.exam_section.item.hysterosonography",
        icon: Scan,
      },
      {
        name: "HSG (Hysterosalpingogram)",
        labelKey: "add_appointment.exam_section.item.hsg",
        icon: Scan,
      },
      {
        name: "Antral follicle count",
        labelKey: "add_appointment.exam_section.item.antral_follicle_count",
        icon: CircleDot,
      },
      {
        name: "Pelvic MRI",
        labelKey: "add_appointment.exam_section.item.pelvic_mri",
        icon: Scan,
      },
    ],
  },
  {
    key: "genetic",
    labelKey: "add_appointment.exam_section.genetic",
    icon: Dna,
    items: [
      {
        name: "Karyotyping",
        labelKey: "add_appointment.exam_section.item.karyotyping",
        icon: Dna,
      },
      {
        name: "Carrier testing",
        labelKey: "add_appointment.exam_section.item.carrier_testing",
        icon: Dna,
      },
      {
        name: "Exome or genome sequencing",
        labelKey: "add_appointment.exam_section.item.exome_sequencing",
        icon: Dna,
      },
    ],
  },
];

const PROCEDURE_SECTIONS: Section[] = [
  {
    key: "retrieval",
    labelKey: "add_appointment.procedure_section.retrieval",
    icon: Egg,
    items: [
      {
        name: "Egg retrieval",
        labelKey: "add_appointment.procedure_section.item.egg_retrieval",
        icon: Egg,
      },
      {
        name: "Sperm collection",
        labelKey: "add_appointment.procedure_section.item.sperm_collection",
        icon: FlaskConical,
      },
      {
        name: "Surgical sperm retrieval",
        labelKey:
          "add_appointment.procedure_section.item.surgical_sperm_retrieval",
        icon: Scissors,
      },
    ],
  },
  {
    key: "transfer",
    labelKey: "add_appointment.procedure_section.transfer",
    icon: ArrowRightLeft,
    items: [
      {
        name: "Embryo transfer",
        labelKey: "add_appointment.procedure_section.item.embryo_transfer",
        icon: ArrowRightLeft,
      },
    ],
  },
  {
    key: "surgical",
    labelKey: "add_appointment.procedure_section.surgical",
    icon: Scissors,
    items: [
      {
        name: "Laparoscopy",
        labelKey: "add_appointment.procedure_section.item.laparoscopy",
        icon: Scissors,
      },
      {
        name: "Hysteroscopy",
        labelKey: "add_appointment.procedure_section.item.hysteroscopy",
        icon: Scissors,
      },
      {
        name: "Endometrial biopsy",
        labelKey: "add_appointment.procedure_section.item.endometrial_biopsy",
        icon: Scissors,
      },
      {
        name: "Testicular biopsy",
        labelKey: "add_appointment.procedure_section.item.testicular_biopsy",
        icon: Scissors,
      },
    ],
  },
];

const COMMON_ACTIVITY_SECTION: Section = {
  key: "common-activities",
  labelKey: "add_appointment.activity_section.common",
  icon: Activity,
  items: [
    {
      name: "Consultation",
      labelKey: "add_appointment.activity.item.consultation",
      icon: MessageSquare,
    },
    {
      name: "Blood tests",
      labelKey: "add_appointment.activity.item.blood_tests",
      icon: Droplets,
    },
    {
      name: "Ultrasound",
      labelKey: "add_appointment.activity.item.ultrasound",
      icon: Scan,
    },
    {
      name: "Counseling",
      labelKey: "add_appointment.activity.item.counseling",
      icon: Heart,
    },
  ],
};

const ALL_ACTIVITY_SECTIONS: Section[] = [
  COMMON_ACTIVITY_SECTION,
  ...EXAM_SECTIONS,
  ...PROCEDURE_SECTIONS,
];

const ALL_ACTIVITY_ITEMS = Array.from(
  new Map(
    ALL_ACTIVITY_SECTIONS.flatMap((section) => section.items).map(
      (item) => [item.name.toLowerCase(), item] as const,
    ),
  ).values(),
);

function findActivityItem(name: string): SubtypeItem | undefined {
  const normalized = name.trim().toLowerCase();
  return ALL_ACTIVITY_ITEMS.find(
    (item) => item.name.toLowerCase() === normalized,
  );
}

function getSectionsForType(type: AppointmentType): Section[] {
  if (type === "exam") return EXAM_SECTIONS;
  if (type === "procedure") return PROCEDURE_SECTIONS;
  return [];
}

function inferTypeFromActivities(activities: string[]): AppointmentType | null {
  const normalized = new Set(
    activities.map((activity) => activity.trim().toLowerCase()),
  );
  if (
    EXAM_SECTIONS.some((section) =>
      section.items.some((item) => normalized.has(item.name.toLowerCase())),
    )
  ) {
    return "exam";
  }
  if (
    PROCEDURE_SECTIONS.some((section) =>
      section.items.some((item) => normalized.has(item.name.toLowerCase())),
    )
  ) {
    return "procedure";
  }
  return null;
}

const APPOINTMENT_TYPES: AppointmentType[] = [
  "exam",
  "procedure",
  "consult",
  "counseling",
];

function defaultActivityForType(type: AppointmentType): string | null {
  if (type === "consult") return "Consultation";
  if (type === "counseling") return "Counseling";
  return null;
}

function normalizeOcrType(raw: string | null): AppointmentType | null {
  if (!raw) return null;
  const lower = raw.toLowerCase().trim();
  return (
    APPOINTMENT_TYPES.find(
      (t) => t === lower || lower.includes(t) || t.includes(lower),
    ) ?? null
  );
}

function findSubtypeMatch(
  type: AppointmentType,
  rawSubtype: string | null,
): SubtypeItem | null {
  if (!rawSubtype) return null;
  const sections = getSectionsForType(type);
  const lower = rawSubtype.toLowerCase().trim();
  for (const section of sections) {
    for (const item of section.items) {
      const itemLower = item.name.toLowerCase();
      if (
        itemLower === lower ||
        itemLower.includes(lower) ||
        lower.includes(itemLower)
      ) {
        return item;
      }
    }
  }
  return null;
}

// ============================================================================
// StepDots
// ============================================================================

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <View
      className="flex-row justify-center"
      style={{
        gap: 6,
        marginBottom: 20,
      }}
    >
      {Array.from({ length: total }).map((_, i) => (
        <View
          key={i}
          style={{
            width: i === current ? 18 : 6,
            height: 6,
            borderRadius: 3,
            backgroundColor:
              i === current
                ? EMBER.colors.warm
                : i < current
                  ? "rgba(184, 74, 22, 0.35)"
                  : "rgba(0, 0, 0, 0.06)",
          }}
        />
      ))}
    </View>
  );
}

// ============================================================================
// Component
// ============================================================================

export function AddAppointmentModal({
  visible,
  onClose,
  editAppointment,
  prefill,
  onSaved,
  onLocalSave,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("AddAppointmentModal");
  const { locale } = useLocale();
  const { height: windowHeight } = useWindowDimensions();
  const createAppointment = useCreateAppointment();
  const updateAppointment = useUpdateAppointment();
  const timezone = useTimezone();
  const transfer = useDocumentSourceTransfer();
  const resetTransfer = transfer.reset;
  const isExtracting =
    transfer.state === "uploading" ||
    transfer.state === "uploaded" ||
    transfer.state === "filing";

  const isEditMode = !!editAppointment;
  /**
   * Opened with its **type** already decided by the caller, so the type grid is
   * a choice that has already been made — scheduling a specific procedure from
   * the treatment hub, for instance.
   *
   * Keyed on `prefill.type`, not on `prefill`. The calendar's add-appointment
   * affordance seeds a date and nothing else, and that caller still needs the
   * grid: suppressing back for it left her on the details step with no type
   * selected, no sections to fill, and closing as her only way out.
   */
  const isSeededCreate = !!prefill?.type && !editAppointment;

  // Step state
  const [step, setStep] = useState(0);

  // Step 0 state
  const [selectedType, setSelectedType] = useState<AppointmentType | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);

  // Step 1 state
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [durationSelection, setDurationSelection] = useState<DurationSelection>(
    DEFAULT_DURATION_MINUTES,
  );
  const [customDurationMinutes, setCustomDurationMinutes] = useState("");
  const [location, setLocation] = useState("");
  const [isOnline, setIsOnline] = useState(false);
  const [meetingLink, setMeetingLink] = useState("");
  const [notes, setNotes] = useState("");

  // Step 2 state
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setDurationFromMinutes = useCallback((durationMinutes: number) => {
    const preset = DURATION_PRESETS.find((value) => value === durationMinutes);
    if (preset) {
      setDurationSelection(preset);
      setCustomDurationMinutes("");
      return;
    }

    setDurationSelection("custom");
    setCustomDurationMinutes(String(durationMinutes));
  }, []);

  const durationMinutes = useMemo(() => {
    if (durationSelection !== "custom") return durationSelection;
    if (!/^\d+$/.test(customDurationMinutes)) return null;
    const parsed = Number(customDurationMinutes);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [customDurationMinutes, durationSelection]);

  const appointmentTimezone = editAppointment?.timeZone || timezone;
  const appointmentEnd = useMemo(
    () =>
      durationMinutes
        ? calculateAppointmentEnd({
            startDate: date,
            startTime: time,
            durationMinutes,
            timeZone: appointmentTimezone,
          })
        : null,
    [appointmentTimezone, date, durationMinutes, time],
  );

  const formatDuration = useCallback(
    (minutes: number) => {
      if (minutes >= 60 && minutes % 60 === 0) {
        return t("add_appointment.duration_hours_value", {
          params: { count: String(minutes / 60) },
        });
      }
      return t("add_appointment.duration_minutes_value", {
        params: { count: String(minutes) },
      });
    },
    [t],
  );

  const formattedEndTime = appointmentEnd
    ? formatTimeHHMM(appointmentEnd.endTime, locale)
    : "";
  const formattedEndValue = appointmentEnd
    ? appointmentEnd.endDate === date
      ? formattedEndTime
      : t("add_appointment.end_value_next_day", {
          params: {
            date: formatDate(appointmentEnd.endDate, locale, {
              weekday: "short",
              month: "short",
              day: "numeric",
            }),
            time: formattedEndTime,
          },
        })
    : "";
  const isClinicalSelection =
    selectedType === "exam" || selectedType === "procedure";
  const appointmentActivities = useMemo(() => {
    const values = [...selectedActivities];
    const customQuery = isClinicalSelection ? searchQuery.trim() : "";
    if (customQuery) values.push(customQuery);

    return Array.from(
      new Map(
        values
          .map((activity) => activity.trim())
          .filter(Boolean)
          .map((activity) => [activity.toLowerCase(), activity] as const),
      ).values(),
    );
  }, [isClinicalSelection, searchQuery, selectedActivities]);
  const hasAppointmentTitle = Boolean(
    title.trim() || appointmentActivities.length > 0,
  );
  const appointmentTitle =
    title.trim() || appointmentActivities[0] || "Appointment";
  const canSubmit = Boolean(
    hasAppointmentTitle &&
    (!isClinicalSelection || appointmentActivities.length > 0) &&
    date.trim() &&
    time.trim() &&
    durationMinutes &&
    appointmentEnd,
  );

  // Save and process the appointment source before using its canonical claims
  // to prefill this form. The full run remains durable beyond this context.
  const handleScanAppointmentLetter = useCallback(async () => {
    const permResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permResult.granted) {
      Alert.alert(
        t("add_appointment.alert_permission_title"),
        t("add_appointment.alert_permission_message"),
      );
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images"],
      quality: 0.8,
    });

    if (result.canceled || !result.assets || result.assets.length === 0) return;

    const asset = result.assets[0]!;
    const file = {
      uri: asset.uri,
      name: asset.fileName || "appointment-letter.jpg",
      mimeType: resolveUploadContentType({
        name: asset.fileName || "appointment-letter.jpg",
        uri: asset.uri,
        mimeType: asset.mimeType,
      }),
    };

    try {
      const source = await transfer.uploadSource(file, {
        destination: "appointment",
        sourceContext: {
          surface: "add_appointment_modal",
          route: "AddAppointmentModal",
          form: "appointment",
          allowBackgroundProcessing: true,
        },
      });
      const outcome = await transfer.processSource(source);
      const projected = projectDocumentProcessingRunToPrefill(
        outcome.run,
        file.name,
      );
      const appointment: DocumentProcessingPrefillAppointment | undefined =
        projected.appointments?.[0];
      const type = normalizeOcrType(appointment?.type || "");
      if (type) {
        setSelectedType(type);
        const match = findSubtypeMatch(
          type,
          appointment?.title || appointment?.type || "",
        );
        if (match) {
          setSelectedActivities([match.name]);
          setTitle(match.name);
        } else if (appointment?.title) {
          setSelectedActivities([appointment.title]);
          setTitle(appointment.title);
        } else {
          const defaultActivity = defaultActivityForType(type);
          setSelectedActivities(defaultActivity ? [defaultActivity] : []);
        }
      } else if (appointment?.title) {
        setTitle(appointment.title);
      }
      const extractedDate = appointment?.date || projected.documentDate;
      if (extractedDate) setDate(extractedDate);
      if (appointment?.time) setTime(appointment.time);
      const extractedDuration =
        appointment?.durationMinutes ||
        (extractedDate && appointment?.time && appointment?.endTime
          ? calculateAppointmentDurationMinutes({
              startDate: extractedDate,
              startTime: appointment.time,
              endDate: appointment.endDate || extractedDate,
              endTime: appointment.endTime,
              timeZone: timezone,
            })
          : null);
      if (extractedDuration) setDurationFromMinutes(extractedDuration);
      if (appointment?.location) setLocation(appointment.location);
      const composedNotes = [appointment?.notes, projected.summary]
        .filter((value): value is string => !!value && value.trim().length > 0)
        .join("\n\n");
      if (composedNotes) setNotes(composedNotes);
      setStep(1);
    } catch (error) {
      console.error("[AddAppointmentModal] canonical processing error:", error);
      Alert.alert(
        t("add_appointment.alert_ocr_failed_title"),
        error instanceof Error
          ? error.message
          : t("add_appointment.alert_ocr_failed_message"),
      );
    }
  }, [t, timezone, transfer, setDurationFromMinutes]);

  // Filtered sections based on search
  const filteredSections = useMemo(() => {
    const sections = selectedType ? getSectionsForType(selectedType) : [];
    if (!searchQuery.trim()) return sections;
    const q = searchQuery.trim().toLowerCase();
    const selected = new Set(
      selectedActivities.map((activity) => activity.trim().toLowerCase()),
    );
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            !selected.has(item.name.toLowerCase()) &&
            (item.name.toLowerCase().includes(q) ||
              t(item.labelKey).toLowerCase().includes(q)),
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [searchQuery, selectedActivities, selectedType, t]);

  const removeActivity = useCallback(
    (name: string) => {
      const normalized = name.trim().toLowerCase();
      setSelectedActivities((current) => {
        const next = current.filter(
          (activity) => activity.trim().toLowerCase() !== normalized,
        );
        if (
          isClinicalSelection &&
          current[0]?.trim().toLowerCase() === normalized
        ) {
          setTitle(next[0] ?? "");
        }
        return next;
      });
    },
    [isClinicalSelection],
  );

  // Reset state on close, or pre-populate for edit mode
  useEffect(() => {
    if (!visible) {
      const timeout = setTimeout(() => {
        setStep(0);
        setSelectedType(null);
        setSearchQuery("");
        setSelectedActivities([]);
        setTitle("");
        setDate("");
        setTime("");
        setDurationSelection(DEFAULT_DURATION_MINUTES);
        setCustomDurationMinutes("");
        setLocation("");
        setIsOnline(false);
        setMeetingLink("");
        setNotes("");
        setIsSaving(false);
        setErrorMessage(null);
        resetTransfer();
      }, 300);
      return () => clearTimeout(timeout);
    }

    if (visible && editAppointment) {
      setTitle(editAppointment.title);
      setDate(editAppointment.date ?? "");
      setTime(editAppointment.time ?? "");
      const restoredDuration =
        editAppointment.date &&
        editAppointment.time &&
        editAppointment.endDate &&
        editAppointment.endTime
          ? calculateAppointmentDurationMinutes({
              startDate: editAppointment.date,
              startTime: editAppointment.time,
              endDate: editAppointment.endDate,
              endTime: editAppointment.endTime,
              timeZone: editAppointment.timeZone || timezone,
            })
          : null;
      setDurationFromMinutes(restoredDuration ?? DEFAULT_DURATION_MINUTES);
      // `subtitle` is presentation copy (for example "Online" or "Clinic
      // visit"), not necessarily a persisted room. Falling back to it here
      // would turn that label into a real meetingRoom on the next save.
      setLocation(editAppointment.location ?? "");
      setIsOnline(editAppointment.isOnline ?? false);
      setMeetingLink(editAppointment.meetingLink ?? "");
      setNotes(editAppointment.notes ?? "");
      // Preserve every persisted activity so editing one appointment detail
      // never collapses a multi-activity visit back to a single subtype.
      const restoredActivities = (editAppointment.consultationType ?? [])
        .map((activity) => activity.trim())
        .filter(Boolean);
      setSelectedType(inferTypeFromActivities(restoredActivities));
      setSelectedActivities(restoredActivities);
      setStep(1);
    }

    // Create flow seeded by a caller (e.g. schedule a beta test from the
    // two-week-wait card). Mirrors the OCR/edit seeding: fill the fields and
    // jump straight to the details step. Never overrides an edit.
    if (visible && prefill && !editAppointment) {
      if (prefill.type) setSelectedType(prefill.type);
      if (prefill.activities) setSelectedActivities(prefill.activities);
      if (prefill.title) setTitle(prefill.title);
      if (prefill.date) setDate(prefill.date);
      setStep(1);
    }
  }, [
    visible,
    editAppointment,
    prefill,
    resetTransfer,
    setDurationFromMinutes,
    timezone,
  ]);

  // Select a category first. The shared dialog action row advances the user
  // after the selection, matching the multi-step flow used by other dialogs.
  const handleTypeSelect = useCallback((type: AppointmentType) => {
    setSelectedType(type);
    setSelectedActivities([]);
    setSearchQuery("");
    setTitle("");
  }, []);

  // Add a canonical exam or procedure to the single details-page selector.
  // Persist the canonical EN name so the displayed title re-localizes via
  // localizeAppointmentTitle when locale changes.
  const handleSubtypeSelect = useCallback((item: SubtypeItem) => {
    setSelectedActivities((current) =>
      current.some(
        (activity) =>
          activity.trim().toLowerCase() === item.name.trim().toLowerCase(),
      )
        ? current
        : [...current, item.name],
    );
    setTitle((current) => current.trim() || item.name);
    setSearchQuery("");
  }, []);

  const handleAdvance = useCallback(() => {
    if (step === 0) {
      if (!selectedType) return;

      // Exam and procedure names are selected directly in the details form.
      // Consult and counseling use their canonical appointment name.
      // Store the canonical EN type name as title so the displayed title
      // re-localizes via localizeAppointmentTitle when locale changes.
      if (selectedType !== "exam" && selectedType !== "procedure") {
        const opt = TYPE_OPTIONS.find((o) => o.value === selectedType);
        const canonical = opt
          ? ((enLocale.add_appointment?.type as Record<string, string>)?.[
              opt.value
            ] ?? selectedType)
          : selectedType;
        setTitle(canonical);
        const defaultActivity = defaultActivityForType(selectedType);
        setSelectedActivities(defaultActivity ? [defaultActivity] : []);
      }
      setStep(1);
    }
  }, [selectedType, step]);

  const handleBack = useCallback(() => {
    if (step === 1) {
      // Back from details to type grid
      setStep(0);
      setSearchQuery("");
      setErrorMessage(null);
      // A seeded create has no grid to go back to. Its type was decided by
      // whatever opened it — "Schedule the transfer" on the hub's transfer step
      // means an embryo transfer, and nothing else — so the modal opened on the
      // details step. Falling back to the type grid offered a choice the caller
      // had already made, on a surface that had no business appearing there.
      // Editing is already treated this way for the same reason.
      if (isEditMode || isSeededCreate) {
        onClose();
        return;
      }

      setSelectedActivities([]);
    } else if (step === 0) {
      if (isEditMode) {
        onClose();
        return;
      }
    }
  }, [isEditMode, isSeededCreate, onClose, step]);

  const handleConfirm = useCallback(async () => {
    // `consultationType` is the deployed string-array field available for
    // the multiple things happening during one visit. Keep canonical names,
    // remove empty/duplicate values, and preserve the entire list on edit.
    const consultationType = appointmentActivities;
    setIsSaving(true);
    setErrorMessage(null);
    try {
      if (!durationMinutes || !appointmentEnd) {
        setErrorMessage(t("add_appointment.error_invalid_duration"));
        setIsSaving(false);
        return;
      }

      // Shared validation authority (#2242): real calendar dates (OCR can
      // produce "March 15" or 2026-02-30), clock-time validity (a missing
      // time used to silently become 09:00), and end-after-start ordering
      // across midnight — identical for every entry point.
      const validation = validateAppointmentDraft({
        title: appointmentTitle,
        startDate: date,
        startTime: time,
        endDate: appointmentEnd.endDate,
        endTime: appointmentEnd.endTime,
      });
      if (!validation.ok) {
        const { fieldErrors } = validation;
        setErrorMessage(
          fieldErrors.startDate || fieldErrors.endDate
            ? t("add_appointment.error_invalid_date")
            : fieldErrors.startTime
              ? t("add_appointment.error_invalid_time")
              : fieldErrors.endTime === "end_before_start"
                ? t("add_appointment.error_end_before_start")
                : fieldErrors.endTime
                  ? t("add_appointment.error_invalid_time")
                  : t("add_appointment.error_invalid_title"),
        );
        setIsSaving(false);
        return;
      }

      const payload = {
        cycleId: isEditMode ? undefined : (prefill?.cycleId ?? null),
        title: appointmentTitle,
        description: notes || null,
        consultationType: consultationType.length ? consultationType : null,
        startDate: date,
        startTime: time,
        endDate: appointmentEnd.endDate,
        endTime: appointmentEnd.endTime,
        timeZone: appointmentTimezone,
        // Format owns the destination field. Clearing the inactive value on
        // save prevents an appointment that changed formats from retaining a
        // stale room or meeting URL in the API.
        meetingRoom: !isOnline && location.trim() ? location.trim() : null,
        isOnline,
        meetingLink: isOnline && meetingLink.trim() ? meetingLink.trim() : null,
      };

      const savedAppointment =
        isEditMode && editAppointment
          ? await updateAppointment.mutateAsync({
              appointmentId:
                editAppointment.appointmentId ?? editAppointment.id,
              ...payload,
            })
          : await createAppointment.mutateAsync(payload);
      onSaved?.({
        appointmentId: savedAppointment?.data?.id ?? null,
        startDate: date,
        startTime: time || null,
        consultationType: consultationType.length ? consultationType : null,
      });
      onClose();
    } catch (error) {
      console.error(
        `[AddAppointmentModal] Failed to ${isEditMode ? "update" : "create"} appointment:`,
        error,
      );
      if (!isEditMode && onLocalSave) {
        onLocalSave({
          title: appointmentTitle,
          consultationType: consultationType.length ? consultationType : null,
          startDate: date,
          startTime: time || undefined,
          endDate: appointmentEnd?.endDate,
          endTime: appointmentEnd?.endTime,
          timeZone: appointmentTimezone,
          location: !isOnline && location.trim() ? location.trim() : undefined,
          notes: notes || undefined,
        });
        onClose();
        return;
      }
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("add_appointment.error_save_failed"),
      );
      setIsSaving(false);
    }
  }, [
    appointmentActivities,
    appointmentTitle,
    notes,
    date,
    time,
    durationMinutes,
    appointmentEnd,
    appointmentTimezone,
    location,
    isOnline,
    meetingLink,
    isEditMode,
    prefill?.cycleId,
    editAppointment,
    createAppointment,
    updateAppointment,
    onClose,
    onSaved,
    onLocalSave,
    t,
  ]);

  // ========== Render helpers ==========

  const showBackButton = step > 0 && !isSeededCreate;
  const totalSteps = 2;
  const dialogBodyMaxHeight = Math.max(220, windowHeight * 0.85 - 220);

  const renderNavigationActions = () => {
    const isTypeSelectionStep = step === 0;
    const canAdvanceFromSelection = Boolean(selectedType);

    return (
      <View
        testID="appointment-dialog-actions"
        style={{
          paddingHorizontal: 20,
          paddingTop: 12,
          paddingBottom: 20,
          borderTopWidth: 1,
          borderTopColor: EMBER.colors.border,
        }}
      >
        {step === 1 && errorMessage ? (
          <View
            style={{
              backgroundColor: "rgba(220, 38, 38, 0.08)",
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "rgba(220, 38, 38, 0.2)",
              padding: 12,
              marginBottom: 12,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 13, color: "#DC2626" }}
            >
              {errorMessage}
            </Text>
          </View>
        ) : null}

        <DialogActions style={{ marginTop: 0 }}>
          <SoftSecondaryButton
            label={
              isTypeSelectionStep || isEditMode || isSeededCreate
                ? t("add_appointment.close")
                : t("add_appointment.go_back")
            }
            onPress={
              isTypeSelectionStep || isSeededCreate ? onClose : handleBack
            }
            flex={1}
          />
          {isTypeSelectionStep ? (
            <DialogPrimaryButton
              label={t("add_appointment.next")}
              onPress={handleAdvance}
              disabled={!canAdvanceFromSelection}
              flex={1}
            />
          ) : (
            <DialogPrimaryButton
              label={
                isSaving
                  ? isEditMode
                    ? t("add_appointment.submitting_edit")
                    : t("add_appointment.submitting_add")
                  : isEditMode
                    ? t("add_appointment.submit_edit")
                    : t("add_appointment.submit_add")
              }
              onPress={handleConfirm}
              disabled={!canSubmit || isSaving}
              loading={isSaving}
              flex={1}
            />
          )}
        </DialogActions>
      </View>
    );
  };

  const renderHeader = () => (
    <View
      style={{
        alignItems: "center",
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: 8,
      }}
    >
      {showBackButton && (
        <Pressable
          onPress={() => {
            // Chevron-only control; its accessibility label is the copy.
            capturePress(
              pressLabel(t("add_appointment.go_back")),
              "add_appointment.go_back",
            );
            handleBack();
          }}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={t("add_appointment.go_back")}
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            width: 28,
            height: 28,
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          <ChevronLeft
            size={20}
            color={EMBER.colors.textLight}
            strokeWidth={2}
          />
        </Pressable>
      )}
      <DialogIcon
        source={APPOINTMENT_ILLUSTRATION}
        width={72}
        height={72}
        style={{ marginBottom: 16 }}
        testID="add-appointment-illustration"
      />
      <DialogHeader
        title={
          step === 0
            ? t("add_appointment.title_new")
            : isEditMode
              ? t("add_appointment.title_edit")
              : t("add_appointment.title_details")
        }
        subtitle={step === 0 ? t("add_appointment.type_intro") : undefined}
        style={{ alignItems: "center" }}
        titleStyle={{ textAlign: "center" }}
        subtitleStyle={{ textAlign: "center" }}
      />
    </View>
  );

  // Step 0a: Type grid
  const renderTypeGrid = () => (
    <>
      <ScrollView
        style={{ maxHeight: dialogBodyMaxHeight, flexShrink: 1 }}
        contentContainerStyle={{ padding: 20 }}
        showsVerticalScrollIndicator={false}
      >
        <StepDots current={0} total={totalSteps} />

        <ActionRowCard
          onPress={isExtracting ? undefined : handleScanAppointmentLetter}
          title={
            isExtracting
              ? t("add_appointment.entry_reading_letter")
              : t("add_appointment.entry_scan_letter")
          }
          subtitle={t("add_appointment.entry_scan_letter_help")}
          leading={
            isExtracting ? (
              <ActivityIndicator size="small" color={EMBER.colors.warm} />
            ) : (
              <Camera size={20} color={EMBER.colors.warm} />
            )
          }
          accessibilityLabel={t("add_appointment.entry_scan_letter")}
          style={{
            marginBottom: 16,
            opacity: isExtracting ? 0.6 : 1,
          }}
        />

        <View
          className="flex-row flex-wrap"
          style={{
            gap: 12,
          }}
        >
          {TYPE_OPTIONS.map((typeOption) => {
            const Icon = typeOption.icon;
            const selected = selectedType === typeOption.value;
            return (
              <Pressable
                key={typeOption.value}
                onPress={() => {
                  capturePress(
                    pressLabel(t(typeOption.labelKey)),
                    "appointment_type",
                  );
                  handleTypeSelect(typeOption.value);
                }}
                style={({ pressed }) => ({
                  width: "47%",
                  borderRadius: 14,
                  borderWidth: 1,
                  borderColor: selected
                    ? EMBER.colors.warm
                    : EMBER.colors.border,
                  backgroundColor: selected
                    ? "rgba(184, 74, 22, 0.08)"
                    : pressed
                      ? "rgba(0,0,0,0.03)"
                      : EMBER.colors.surface,
                  padding: 16,
                  alignItems: "center",
                  gap: 8,
                })}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                aria-selected={selected}
              >
                <Icon
                  size={24}
                  color={selected ? EMBER.colors.warm : EMBER.colors.textMid}
                  strokeWidth={1.5}
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: selected ? "600" : "500",
                    color: selected ? EMBER.colors.warm : EMBER.colors.text,
                  }}
                >
                  {t(typeOption.labelKey)}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </ScrollView>
      {renderNavigationActions()}
    </>
  );

  const renderClinicalActivityField = () => {
    const hasSearchQuery = Boolean(searchQuery.trim());
    const fieldLabel =
      selectedType === "exam"
        ? t("add_appointment.exam_label")
        : t("add_appointment.procedure_label");
    const placeholder =
      selectedType === "exam"
        ? t("add_appointment.what_exam_placeholder")
        : t("add_appointment.what_procedure_placeholder");

    return (
      <View style={{ marginBottom: 16 }} testID="appointment-clinical-field">
        <MoonFormField
          label={fieldLabel}
          labelIcon={selectedType === "exam" ? Stethoscope : Syringe}
        >
          <View style={{ gap: 8 }}>
            <MoonSearchInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              onClear={() => setSearchQuery("")}
              placeholder={placeholder}
            />

            {selectedActivities.length ? (
              <View className="flex-row flex-wrap" style={{ gap: 8 }}>
                {selectedActivities.map((activity) => {
                  const label = localizeAppointmentTitle(activity, t);
                  const item = findActivityItem(activity);
                  return (
                    <Pressable
                      key={activity}
                      onPress={() => {
                        // Icon-only control; its accessibility label is the copy.
                        capturePress(
                          pressLabel(
                            t("add_appointment.activity_remove_accessibility", {
                              params: { activity: label },
                            }),
                          ),
                          "remove_appointment_activity",
                        );
                        removeActivity(activity);
                      }}
                      accessibilityRole="button"
                      accessibilityLabel={t(
                        "add_appointment.activity_remove_accessibility",
                        { params: { activity: label } },
                      )}
                    >
                      <Tag
                        label={label}
                        variant="outline"
                        icon={item?.icon}
                        trailing={
                          <X
                            size={10}
                            color={EMBER.colors.textLight}
                            strokeWidth={2}
                          />
                        }
                      />
                    </Pressable>
                  );
                })}
              </View>
            ) : null}

            {hasSearchQuery &&
              filteredSections.map((section) => {
                const SectionIcon = section.icon;
                return (
                  <View key={section.key} style={{ marginTop: 8 }}>
                    <View
                      className="flex-row items-center"
                      style={{ gap: 8, marginBottom: 8 }}
                    >
                      <SectionIcon
                        size={16}
                        color={EMBER.colors.warm}
                        strokeWidth={1.5}
                      />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          fontWeight: "700",
                          color: EMBER.colors.warm,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {t(section.labelKey)}
                      </Text>
                    </View>

                    <View
                      style={{
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: EMBER.colors.border,
                        backgroundColor: EMBER.colors.surface,
                        overflow: "hidden",
                      }}
                    >
                      {section.items.map((item, idx) => {
                        const ItemIcon = item.icon;
                        const isLast = idx === section.items.length - 1;
                        return (
                          <Pressable
                            key={item.name}
                            onPress={() => {
                              capturePress(
                                pressLabel(t(item.labelKey)),
                                "appointment_subtype",
                              );
                              handleSubtypeSelect(item);
                            }}
                            className="flex-row items-center"
                            style={{
                              gap: 12,
                              minHeight: 48,
                              flexShrink: 0,
                              justifyContent: "center",
                              paddingVertical: 13,
                              paddingHorizontal: 14,
                              borderBottomWidth: isLast ? 0 : 1,
                              borderBottomColor: EMBER.colors.border,
                            }}
                            accessibilityRole="button"
                          >
                            <ItemIcon
                              size={18}
                              color={EMBER.colors.textLight}
                              strokeWidth={1.5}
                            />
                            <Text
                              className="font-dm-sans"
                              style={{
                                flex: 1,
                                fontSize: 15,
                                color: EMBER.colors.text,
                              }}
                            >
                              {t(item.labelKey)}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>
                );
              })}

            {hasSearchQuery && filteredSections.length === 0 ? (
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.textMid,
                  fontSize: 14,
                  marginTop: 8,
                  textAlign: "center",
                }}
              >
                {t("add_appointment.no_results")}
              </Text>
            ) : null}
          </View>
        </MoonFormField>
      </View>
    );
  };

  const renderStep1 = () => (
    <>
      <ScrollView
        style={{ maxHeight: dialogBodyMaxHeight, flexShrink: 1 }}
        contentContainerStyle={{ padding: 20 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <StepDots current={1} total={totalSteps} />

        {isClinicalSelection ? (
          renderClinicalActivityField()
        ) : (
          <View style={{ marginBottom: 16 }}>
            <MoonFormField
              label={t("add_appointment.title_label")}
              labelIcon={
                TYPE_OPTIONS.find((option) => option.value === selectedType)
                  ?.icon
              }
            >
              <MoonFieldInput>
                <TextInput
                  value={localizeAppointmentTitle(title, t)}
                  onChangeText={setTitle}
                  placeholder={t("add_appointment.title_placeholder")}
                  placeholderTextColor={EMBER.colors.textFaint}
                  style={{
                    paddingVertical: 12,
                    fontSize: 15,
                    color: EMBER.colors.text,
                    fontFamily: "DMSans_400Regular",
                  }}
                />
              </MoonFieldInput>
            </MoonFormField>
          </View>
        )}

        {/* Date and time are one appointment-start row. */}
        <View className="flex-row" style={{ gap: 12, marginBottom: 16 }}>
          <View style={{ flex: 1 }}>
            <MoonDateField
              label={t("add_appointment.date_label")}
              labelIcon={Calendar}
              value={date}
              onChange={setDate}
            />
          </View>
          <View style={{ flex: 1 }}>
            <MoonTimeField
              label={t("add_appointment.time_label")}
              labelIcon={Clock}
              value={time}
              onChange={setTime}
            />
          </View>
        </View>

        {/* Duration is explicit patient input. The selected value determines
          the persisted end date/time, including rollover after midnight. */}
        <View style={{ marginBottom: 16 }}>
          <MoonFormField
            label={t("add_appointment.duration_label")}
            labelIcon={Timer}
            error={
              durationSelection === "custom" && !durationMinutes
                ? t("add_appointment.error_invalid_duration")
                : undefined
            }
            hint={
              appointmentEnd
                ? t("add_appointment.end_preview", {
                    params: { value: formattedEndValue },
                  })
                : t("add_appointment.duration_hint")
            }
          >
            <View className="flex-row flex-wrap" style={{ gap: 8 }}>
              {DURATION_PRESETS.map((preset) => {
                const selected = durationSelection === preset;
                const isDefault = preset === DEFAULT_DURATION_MINUTES;
                const label = formatDuration(preset);
                return (
                  <MoonFilterChip
                    key={preset}
                    label={label}
                    selected={selected}
                    onPress={() => {
                      setDurationSelection(preset);
                      setCustomDurationMinutes("");
                    }}
                    accessibilityLabel={
                      isDefault
                        ? t("add_appointment.duration_default_accessibility", {
                            params: { value: label },
                          })
                        : label
                    }
                    radius={10}
                    showCheck={false}
                  />
                );
              })}

              <MoonFilterChip
                label={t("add_appointment.duration_custom")}
                selected={durationSelection === "custom"}
                onPress={() => setDurationSelection("custom")}
                radius={10}
                showCheck={false}
              />
            </View>

            {durationSelection === "custom" ? (
              <MoonFieldInput style={{ marginTop: 10 }}>
                <TextInput
                  value={customDurationMinutes}
                  onChangeText={(value) =>
                    setCustomDurationMinutes(value.replace(/\D/g, ""))
                  }
                  accessibilityLabel={t(
                    "add_appointment.duration_custom_accessibility",
                  )}
                  placeholder={t("add_appointment.duration_custom_placeholder")}
                  placeholderTextColor={EMBER.colors.textFaint}
                  keyboardType="number-pad"
                  maxLength={5}
                  style={{
                    paddingVertical: 12,
                    fontSize: 15,
                    color: EMBER.colors.text,
                    fontFamily: "DMSans_400Regular",
                  }}
                />
              </MoonFieldInput>
            ) : null}
          </MoonFormField>
        </View>

        {/* Format */}
        <MoonFormField
          label={t("add_appointment.format_label")}
          labelIcon={Video}
          style={{ marginBottom: 16 }}
        >
          <View className="flex-row flex-wrap" style={{ gap: 8 }}>
            <MoonFilterChip
              label={t("add_appointment.format_in_person")}
              icon={MapPin}
              selected={!isOnline}
              showCheck={false}
              onPress={() => setIsOnline(false)}
            />
            <MoonFilterChip
              label={t("add_appointment.format_online")}
              icon={Video}
              selected={isOnline}
              showCheck={false}
              onPress={() => setIsOnline(true)}
            />
          </View>
        </MoonFormField>

        {/* The selected format determines which destination is relevant. */}
        {!isOnline && (
          <View style={{ marginBottom: 16 }}>
            <MoonFormField
              label={t("add_appointment.location_label")}
              labelIcon={MapPin}
            >
              <MoonFieldInput>
                <View className="flex-row items-center">
                  <TextInput
                    value={location}
                    onChangeText={setLocation}
                    placeholder={t("add_appointment.location_placeholder")}
                    placeholderTextColor={EMBER.colors.textFaint}
                    style={{
                      flex: 1,
                      paddingVertical: 12,
                      fontSize: 15,
                      color: EMBER.colors.text,
                      fontFamily: "DMSans_400Regular",
                    }}
                  />
                </View>
              </MoonFieldInput>
            </MoonFormField>
          </View>
        )}

        {isOnline && (
          <View style={{ marginBottom: 16 }}>
            <MoonFormField
              label={t("add_appointment.meeting_link_label")}
              labelIcon={Link}
            >
              <MoonFieldInput>
                <TextInput
                  value={meetingLink}
                  onChangeText={setMeetingLink}
                  placeholder={t("add_appointment.meeting_link_placeholder")}
                  placeholderTextColor={EMBER.colors.textFaint}
                  autoCapitalize="none"
                  keyboardType="url"
                  style={{
                    paddingVertical: 12,
                    fontSize: 15,
                    color: EMBER.colors.text,
                    fontFamily: "DMSans_400Regular",
                  }}
                />
              </MoonFieldInput>
            </MoonFormField>
          </View>
        )}

        {/* Notes */}
        <View style={{ marginBottom: 24 }}>
          <MoonFormField
            label={t("add_appointment.notes_optional")}
            labelIcon={FileText}
          >
            <MoonFieldInput>
              <TextInput
                value={notes}
                onChangeText={setNotes}
                placeholder={t("add_appointment.notes_placeholder")}
                placeholderTextColor={EMBER.colors.textFaint}
                multiline
                numberOfLines={3}
                style={{
                  paddingVertical: 12,
                  fontSize: 15,
                  color: EMBER.colors.text,
                  minHeight: 80,
                  textAlignVertical: "top",
                  fontFamily: "DMSans_400Regular",
                }}
              />
            </MoonFieldInput>
          </MoonFormField>
        </View>
      </ScrollView>
      {renderNavigationActions()}
    </>
  );

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      animationType="fade"
      closeButton="minimal"
      containerStyle={{
        width: "90%",
        maxWidth: 420,
      }}
      surfaceStyle={{
        maxHeight: windowHeight * 0.85,
      }}
      // Dialog's centered layout has a generous default inset. The dialog
      // owns its own header, scroll padding, and action footer, so clear all
      // four edges here to avoid stacking that inset with each step's padding.
      contentStyle={{
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
      }}
      testID="add-appointment-modal"
    >
      {renderHeader()}
      {step === 0 && renderTypeGrid()}
      {step === 1 && renderStep1()}
    </Dialog>
  );
}
